import { useCallback } from "react";
import { usePostHog } from "posthog-js/react";
import {
  useDatadog,
  FORM_LAST_USER_ID,
  FORM_LAST_EMAIL,
  FORM_LAST_OLD_USER_ID,
} from "@auth/client-sdk-react";

import { useAnalytics } from "apps/website/hooks/useAnalytics";
import {
  getShreddedAaSetOnceProperty,
} from "apps/website/utils/tracking/shreddedAaPersonProperties";

export interface ISyncCustomerIdentityParams {
  newUserId?: string;
  oldUserId?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

/**
 * Pushes the customer's identity to our analytics + observability layers:
 * Segment/analytics `identify`, PostHog `identify`, and the Datadog global
 * context + `form_last_*` localStorage breadcrumbs.
 *
 * Shared by the CAT_PARENT_DETAILS submit (`useCreateFreshCustomer`) and the
 * checkout account sync (`useCheckout`) so a name/email entered or changed on
 * either surface keeps these layers consistent. It deliberately does NOT fire
 * lead/GTM events — those remain owned by the individual call sites so they
 * only fire once, at the point the lead is captured.
 */
export const useSyncCustomerIdentity = () => {
  const posthog = usePostHog();
  const analytics = useAnalytics();
  const datadog = useDatadog();

  return useCallback(
    ({ newUserId, oldUserId, email, firstName, lastName }: ISyncCustomerIdentityParams) => {
      if (oldUserId) {
        const fullName = [ firstName, lastName ].filter(Boolean).join(" ") || undefined;

        // Identify user in analytics
        void analytics
          .identify(oldUserId, {
            email,
            name: fullName,
          })
          .catch((e) => {
            console.error("Failed to identify user", e);
          });

        // Identify user in PostHog
        const personProperties = { email, firstName, lastName };
        const shreddedAaSetOnceProps = getShreddedAaSetOnceProperty({ posthog });
        if (shreddedAaSetOnceProps) {
          posthog.identify(oldUserId, personProperties, shreddedAaSetOnceProps);
        } else {
          posthog.identify(oldUserId, personProperties);
        }

        datadog.setGlobalContextProperty(FORM_LAST_OLD_USER_ID, oldUserId);
        localStorage.setItem(FORM_LAST_OLD_USER_ID, oldUserId);
      }

      // Update Datadog context and localStorage
      if (newUserId) {
        datadog.setGlobalContextProperty(FORM_LAST_USER_ID, newUserId);
        localStorage.setItem(FORM_LAST_USER_ID, newUserId);
      }

      if (email) {
        datadog.setGlobalContextProperty(FORM_LAST_EMAIL, email);
        localStorage.setItem(FORM_LAST_EMAIL, email);
      }
    },
    [ posthog, analytics, datadog ],
  );
};
